import React, { ReactElement, useCallback, useMemo } from 'react'
import {
    ComponentType,
    GenericContentType,
    LayoutComponentProps,
    LayoutProps,
    TeaserProps,
} from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FlexingContainer from '@sport1/news-styleguide/FlexingContainer'
import AdSlot from '@/components/Ads/AdSlot'
import FanVoting from '@/components/FanVoting'
import TippspielSport from '@/components/TippspielSport'
import MatchElementsCard from '@/components/MatchElementsCard'
import GenericComponent from '@/components/Generic'
import MatchInfos from '@/components/MatchInfos'
import MatchFacts from '@/components/MatchFacts'
import LivetickerTeaser from '@/components/LivetickerTeaser'
import { useLivetickerMatch } from '@/utils/liveticker/LivetickerMatchProvider'
import { isMobileView } from '@/helpers/pageHelper'
import { OddsDisclaimerContextProvider } from '@/utils/oddsDisclaimer/OddsDisclaimerProvider'
import OptaWidget from '@/components/OptaWidget'

type LivetickerContentLayoutProps = {
    layoutData: LayoutProps
}

const LivetickerContentLayout = ({ layoutData }: LivetickerContentLayoutProps) => {
    const { match } = useLivetickerMatch()
    const isMobile = isMobileView()
    const shouldAdOnlyShowOnMobile = useCallback(() => {
        return layoutData.components.some(
            c =>
                c.position !== 'right' &&
                c.type === ComponentType.GENERIC &&
                c.contentType === GenericContentType.MATCH_LINEUP
        )
    }, [layoutData])

    const renderLivetickerComponent = useCallback(
        ({ component }: { component: LayoutComponentProps }): ReactElement | null => {
            let pageItem = null
            switch (component.type) {
                case ComponentType.AD:
                    /** some ads shouldn't be shown on desktop because they are included into the component layout */
                    const showOnlyOnMobile = shouldAdOnlyShowOnMobile()
                    /** also: the first banner is taken care of by
                     * firstBillboard or eventTopAd */
                    const isFirstBanner =
                        (component.id === 'banner' || component.id === 'topmobile') &&
                        component.ad.placement === 'BANNER'
                    pageItem =
                        !isFirstBanner && (!showOnlyOnMobile || (showOnlyOnMobile && isMobile)) ? (
                            <NonFlexingContainer
                                marginX={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-none']}
                            >
                                <AdSlot
                                    width="100%"
                                    marginRight="spacing-none"
                                    hasBorder={component.position === 'right'}
                                    {...component}
                                />
                            </NonFlexingContainer>
                        ) : null
                    break
                case ComponentType.MATCH_FACTS:
                    pageItem = <MatchFacts {...component} />
                    break
                case ComponentType.FAN_VOTING:
                    pageItem = <FanVoting {...component} />
                    break
                case ComponentType.MATCH_INFOS:
                    pageItem = <MatchInfos {...component} isRightColumn />
                    break
                case ComponentType.MATCH_ELEMENTS_CARD:
                    pageItem = (
                        <MatchElementsCard {...component} trackingInteractionLabel="liveticker" />
                    )
                    break
                case ComponentType.TIPPSPIEL_SPORT:
                    pageItem = <TippspielSport {...component} />
                    break
                case ComponentType.GENERIC:
                    pageItem = (
                        <OddsDisclaimerContextProvider>
                            <GenericComponent layoutData={layoutData} {...component} />
                        </OddsDisclaimerContextProvider>
                    )
                    break
                case ComponentType.TEASER_CARD:
                    // TODO: TeaserTracking?
                    const componentWithoutAds = {
                        ...component,
                        content: component.content.filter(
                            (c): c is TeaserProps => c.type !== ComponentType.AD
                        ),
                    } //LivetickerTeaser shouldn't contain ads
                    pageItem = <LivetickerTeaser {...componentWithoutAds} />
                    break
                case ComponentType.OPTA_WIDGET:
                    pageItem = <OptaWidget disableSpaceX {...component} />
                    break
            }
            return pageItem
        },
        [shouldAdOnlyShowOnMobile, isMobile, layoutData]
    )

    const { leftComponents, rightComponents } = useMemo(() => {
        const leftComponents: LayoutComponentProps[] = [],
            rightComponents: LayoutComponentProps[] = []
        layoutData.components.forEach(comp => {
            if (comp.position === 'right') {
                // filter useless standings out
                if (
                    comp.type === ComponentType.GENERIC &&
                    comp.contentType === GenericContentType.MATCH_STANDINGS &&
                    match?.competition &&
                    !match.competition.hasStandings
                ) {
                    return
                }
                rightComponents.push(comp)
            } else {
                leftComponents.push(comp)
            }
        })
        return {
            leftComponents,
            rightComponents,
        }
    }, [layoutData, match])

    return (
        <>
            <NonFlexingContainer
                className="bg-none md:bg-white"
                horizontal
                padding={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-11']}
                marginBottom="spacing-11"
                flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}
                testID="liveticker-content-container"
            >
                <NonFlexingContainer
                    width={rightComponents.length ? ['100%', '100%', '100%', '650px'] : '100%'}
                    paddingTop="spacing-6"
                    paddingX={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-none']}
                >
                    {leftComponents.map((component, index) => (
                        <React.Fragment key={`${component.type}_${index}`}>
                            {renderLivetickerComponent({ component })}
                        </React.Fragment>
                    ))}
                </NonFlexingContainer>
                {rightComponents.length ? (
                    <FlexingContainer
                        minWidth={100}
                        marginLeft={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-9']}
                        paddingTop={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-7']}
                    >
                        {rightComponents.map((component, index) => (
                            <React.Fragment key={`${component.type}_${index}`}>
                                {renderLivetickerComponent({ component })}
                            </React.Fragment>
                        ))}
                    </FlexingContainer>
                ) : null}
            </NonFlexingContainer>
        </>
    )
}

export default LivetickerContentLayout
